import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import productionService from '../../services/productionService';
import { resolveWorkshopEntryFromRaw } from '../../services/lotScanResolve';
import { useAuth } from '../../contexts/AuthContext';
import {
  userCanAccessProductionTrackingDashboard,
  userIsMobileWorkstationOnly,
} from '../../lib/operatorRoles';
import QRScanner from '../qr-code-production-tracking/components/QRScanner';
import {
  WORKSHOP_STEPS,
  getNextWorkshopStatus,
  isWorkshopTerminal,
  workshopStepHintForStatus,
  workshopStepKeyForStatus,
  workshopStepIndexForKey,
} from '../../config/workshopLotWorkflow';
import { fetchProductionPhasesForLot, pickActiveProductionPhase } from '../../services/caseProductionPhasesService';
import WorkshopProductionPhasesPanel from './WorkshopProductionPhasesPanel';
import WorkshopDocumentBar, { WORKSHOP_DOCUMENT_BAR_HEIGHT } from './WorkshopDocumentBar';
import WorkshopLotHeader from './WorkshopLotHeader';
import {
  getWorkshopStationRoleTypes,
  pickWorkshopPhaseForRole,
  resolvePrimaryWorkshopRole,
  userHasExpandedWorkshopView,
} from '../../lib/workshopOperatorView';
import WorkshopStationRolePicker from './WorkshopStationRolePicker';
import { buildEmptyIpr, normalizeIprData } from '../../lib/iprToothData';

function WorkshopStepper({ status }) {
  const key = workshopStepKeyForStatus(status);
  const currentIdx = workshopStepIndexForKey(key);
  const terminal = isWorkshopTerminal(status);

  return (
    <div className="w-full overflow-x-auto pb-1">
      <div className="flex items-center gap-1 min-w-max px-1">
        {WORKSHOP_STEPS.map((step, i) => {
          const done = terminal ? i <= currentIdx : i < currentIdx;
          const active = i === currentIdx && !terminal;
          return (
            <React.Fragment key={step.key}>
              {i > 0 && (
                <div
                  className={`h-0.5 w-6 shrink-0 rounded ${done || active ? 'bg-primary' : 'bg-muted'}`}
                  aria-hidden
                />
              )}
              <div
                className={`flex flex-col items-center gap-1 px-2 py-1 rounded-lg min-w-[4.5rem] ${
                  active ? 'bg-primary/15 ring-2 ring-primary/40' : done ? 'bg-success/10' : 'bg-muted/50'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    done ? 'bg-success text-white' : active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </div>
                <span className={`text-[10px] font-medium text-center leading-tight max-w-[5.5rem] ${active ? 'text-primary' : 'text-muted-foreground'}`}>
                  {step.label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function WorkshopLotPage() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const batchId = searchParams.get('batch');

  const [roleTypesList, setRoleTypesList] = useState(() => userProfile?.roleTypes ?? []);

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(!!batchId);
  const [loadError, setLoadError] = useState(null);
  const [advancing, setAdvancing] = useState(false);
  const [iprData, setIprData] = useState(null);
  const [iprLoading, setIprLoading] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [productionPhases, setProductionPhases] = useState([]);
  const [phasesLoading, setPhasesLoading] = useState(false);
  const [caseDataMin, setCaseDataMin] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [activeStationRole, setActiveStationRole] = useState(null);

  const loadBatch = useCallback(async (id) => {
    if (!id) return undefined;
    setLoading(true);
    setLoadError(null);
    try {
      const b = await productionService.getById(id);
      if (!b?.id) throw new Error('Lot introuvable');
      setBatch(b);
      return b;
    } catch (e) {
      setBatch(null);
      setLoadError(e.message || 'Impossible de charger le lot');
      return undefined;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (batchId) loadBatch(batchId);
    else {
      setBatch(null);
      setLoadError(null);
      setLoading(false);
      setProductionPhases([]);
      setCaseDataMin(null);
    }
  }, [batchId, loadBatch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;
      const { data: rows } = await supabase
        .from('user_role_assignments')
        .select('role:user_roles(role_type)')
        .eq('user_id', user.id)
        .eq('is_active', true);
      if (cancelled) return;
      const types = (rows || [])
        .map((r) => r.role?.role_type)
        .filter(Boolean);
      setRoleTypesList(types);
      const primary = resolvePrimaryWorkshopRole(types) || 'user';
      setUserRole(primary);
      const stations = getWorkshopStationRoleTypes(types);
      setActiveStationRole((prev) => {
        if (prev && stations.includes(prev)) return prev;
        return stations[0] || null;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const caseId = batch?.case_id;
    if (!caseId) {
      setCaseDataMin(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('cases')
        .select(
          'id, case_number, priority, deadline, user_profile_id, machine_id, patient:patients!cases_patient_id_fkey(name)'
        )
        .eq('id', caseId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setCaseDataMin(null);
        return;
      }
      setCaseDataMin({
        id: data.id,
        caseNumber: data.case_number,
        patientName: data.patient?.name,
        patient_name: data.patient?.name,
        clinicName: batch?.clinicName,
        clinic_name: batch?.clinicName,
        priority: data.priority ?? batch?.priority,
        deadline: data.deadline,
        dentistId: data.user_profile_id,
        machineId: data.machine_id,
        machine_id: data.machine_id,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [batch?.case_id, batch?.clinicName, batch?.priority]);

  useEffect(() => {
    const caseId = batch?.case_id;
    const groupKey = batch?.current_phase;
    const hasGroup =
      groupKey != null && String(groupKey).trim() !== '';

    if (!caseId || !hasGroup) {
      setProductionPhases([]);
      setPhasesLoading(false);
      return;
    }

    let cancelled = false;
    setPhasesLoading(true);
    fetchProductionPhasesForLot(caseId, groupKey).then(({ phases, error }) => {
      if (cancelled) return;
      if (error) toast.error('Impossible de charger les phases atelier pour ce lot.');
      setProductionPhases(phases || []);
      setPhasesLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [batch?.case_id, batch?.current_phase]);

  useEffect(() => {
    const caseId = batch?.case_id;
    if (!caseId) {
      setIprData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setIprLoading(true);
      try {
        const { data, error } = await supabase
          .from('ipr_stripping_sheets')
          .select('ipr_data, total_ipr_mm')
          .eq('case_id', caseId)
          .maybeSingle();
        if (cancelled) return;
        if (error) {
          setIprData(null);
          return;
        }
        if (data?.ipr_data) setIprData(normalizeIprData(data.ipr_data));
        else setIprData(buildEmptyIpr());
      } finally {
        if (!cancelled) setIprLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [batch?.case_id]);

  const onScanPayload = async (raw) => {
    const { batch: b, error, info } = await resolveWorkshopEntryFromRaw(raw);
    if (b?.id) {
      setSearchParams({ batch: b.id });
      if (info) {
        toast.success('Lot ouvert', { description: info });
      } else {
        toast.success('Lot ouvert');
      }
    } else {
      toast.error(error || 'Lot introuvable');
    }
  };

  const refreshWorkshopTimeline = useCallback(async () => {
    if (!batchId) return;
    const b = await loadBatch(batchId);
    if (!b?.case_id) return;
    if (b.current_phase == null || String(b.current_phase).trim() === '') return;
    const { phases, error } = await fetchProductionPhasesForLot(b.case_id, b.current_phase);
    if (error) toast.error('Impossible de rafraîchir les phases atelier.');
    setProductionPhases(phases || []);
  }, [batchId, loadBatch]);

  const handleAdvance = async () => {
    if (!batch?.id) return;
    const next = getNextWorkshopStatus(batch.status);
    if (!next) return;
    setAdvancing(true);
    try {
      await productionService.update(batch.id, {
        status: next,
        updatedAt: new Date().toISOString(),
      });
      toast.success('Étape enregistrée', { description: 'Le lot a été mis à jour.' });
      await loadBatch(batch.id);
    } catch (e) {
      toast.error(e.message || 'Mise à jour impossible');
    } finally {
      setAdvancing(false);
    }
  };

  const key = batch ? workshopStepKeyForStatus(batch.status) : null;
  const stepIdx = key != null ? workshopStepIndexForKey(key) : 0;
  const progressPct = Math.round(((stepIdx + 1) / WORKSHOP_STEPS.length) * 100);

  const timelineEligible =
    Boolean(batch?.case_id) &&
    batch?.current_phase != null &&
    String(batch.current_phase).trim() !== '';
  const showTimelineLoading = timelineEligible && phasesLoading;
  const showTimelinePanel = timelineEligible && !phasesLoading && productionPhases.length > 0;
  const showLegacyWorkshopFlow =
    !timelineEligible || (!phasesLoading && timelineEligible && productionPhases.length === 0);

  const workshopCaseData =
    caseDataMin ||
    (batch?.case_id
      ? {
          id: batch.case_id,
          caseNumber: null,
          patientName: batch.patientName,
          patient_name: batch.patientName,
          clinicName: batch.clinicName,
          clinic_name: batch.clinicName,
          priority: batch.priority,
          deadline: batch.deadline,
          dentistId: null,
          machineId: batch.machineId,
          machine_id: batch.machineId,
        }
      : null);

  const effectiveRoleTypes = useMemo(() => {
    if (Array.isArray(roleTypesList) && roleTypesList.length > 0) return roleTypesList;
    return userProfile?.roleTypes ?? [];
  }, [roleTypesList, userProfile?.roleTypes]);

  const stationRoles = useMemo(
    () => getWorkshopStationRoleTypes(effectiveRoleTypes),
    [effectiveRoleTypes]
  );

  const expandedWorkshopView = userHasExpandedWorkshopView(
    resolvePrimaryWorkshopRole(effectiveRoleTypes)
  );

  const effectiveUserRole = useMemo(() => {
    if (expandedWorkshopView) {
      return resolvePrimaryWorkshopRole(effectiveRoleTypes) || userRole || 'user';
    }
    if (stationRoles.length >= 2 && activeStationRole) {
      return activeStationRole;
    }
    return resolvePrimaryWorkshopRole(effectiveRoleTypes) || userRole || 'user';
  }, [expandedWorkshopView, stationRoles, activeStationRole, effectiveRoleTypes, userRole]);

  const permissionCodes = userProfile?.permissionCodes ?? [];
  const showBureauButton = userCanAccessProductionTrackingDashboard(effectiveRoleTypes, permissionCodes);
  const mobileWorkstationOnly = userIsMobileWorkstationOnly(effectiveRoleTypes);

  const displayPhase = useMemo(() => {
    if (!productionPhases.length) return null;
    if (expandedWorkshopView) return pickActiveProductionPhase(productionPhases);
    return pickWorkshopPhaseForRole(productionPhases, effectiveUserRole);
  }, [productionPhases, expandedWorkshopView, effectiveUserRole]);

  const showDocumentBar = Boolean(batch && !loading && !loadError);
  const mainBottomPad = showDocumentBar
    ? `calc(${WORKSHOP_DOCUMENT_BAR_HEIGHT} + 1.5rem + env(safe-area-inset-bottom))`
    : 'max(1rem, env(safe-area-inset-bottom))';

  const handleManualOpen = () => {
    const raw = manualCode.trim();
    if (!raw) {
      toast.error('Saisissez un numéro de lot, de cas ou un code QR');
      return;
    }
    onScanPayload(raw);
  };

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col pb-[max(1rem,env(safe-area-inset-bottom))]">
      <header className="sticky top-0 z-20 bg-card/95 backdrop-blur border-b border-border px-3 py-3 flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="min-h-[44px] px-2"
          iconName="ArrowLeft"
          onClick={() => {
            if (mobileWorkstationOnly) {
              setSearchParams({});
              return;
            }
            navigate('/dashboard');
          }}
          aria-label="Retour"
        />
        <div className="flex-1 min-w-0 text-center">
          <p className="text-xs text-muted-foreground truncate">Suivi de production</p>
          <p className="text-sm font-semibold text-foreground truncate">
            {batch ? `${batch.patientName || 'Patient'} · ${batch.batchNumber || 'Lot'}` : 'Scanner ou saisir un code'}
          </p>
        </div>
        {showBureauButton ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-[44px] shrink-0 text-xs"
            onClick={() => navigate('/production-tracking-dashboard')}
          >
            Bureau
          </Button>
        ) : (
          <span className="w-[72px] shrink-0" aria-hidden />
        )}
      </header>

      <main
        className="flex-1 flex flex-col p-3 sm:p-4 gap-3 sm:gap-4 max-w-lg mx-auto w-full pt-1"
        style={{ paddingBottom: mainBottomPad }}
      >
        {!batchId && (
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-center">
              <p className="text-sm font-medium text-foreground">Ouvrir un lot</p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Scannez le QR du lot, ou saisissez le numéro de lot, le lien, le numéro de cas ou l’UUID du cas.
              </p>
            </div>
            <QRScanner layout="workshop" onScan={onScanPayload} />
            <div className="space-y-2">
              <label htmlFor="workshop-manual-code" className="text-xs font-medium text-muted-foreground px-1">
                Ou saisir le code
              </label>
              <input
                id="workshop-manual-code"
                type="text"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleManualOpen()}
                placeholder="N° lot, n° cas, lien QR…"
                className="w-full min-h-[52px] px-4 rounded-xl border border-border bg-card text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                autoComplete="off"
              />
              <Button type="button" className="w-full min-h-[52px] text-base font-semibold" onClick={handleManualOpen}>
                Ouvrir le lot
              </Button>
            </div>
          </div>
        )}

        {batchId && loading && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            <p className="text-sm text-muted-foreground">Chargement du lot…</p>
          </div>
        )}

        {batchId && !loading && loadError && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-center space-y-4">
            <Icon name="AlertCircle" className="mx-auto text-destructive" size={40} />
            <p className="text-destructive font-medium">{loadError}</p>
            <Button className="min-h-[48px] w-full" variant="default" onClick={() => setSearchParams({})}>
              Scanner un autre code
            </Button>
          </div>
        )}

        {batch && !loading && !loadError && (
          <>
            <WorkshopStationRolePicker
              stationRoles={stationRoles}
              activeRole={activeStationRole}
              onChange={setActiveStationRole}
            />

            <WorkshopLotHeader
              batch={batch}
              caseData={workshopCaseData}
              productionPhases={productionPhases}
              displayPhase={displayPhase}
              userRole={effectiveUserRole}
            />

            {showTimelineLoading && (
              <div className="rounded-xl border border-border bg-card p-6 flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                <p className="text-sm text-muted-foreground text-center">
                  Chargement du parcours cas (phases production)…
                </p>
              </div>
            )}

            {showTimelinePanel && batch.case_id && workshopCaseData && (
              <WorkshopProductionPhasesPanel
                batch={batch}
                caseId={batch.case_id}
                caseData={workshopCaseData}
                phases={productionPhases}
                userRole={effectiveUserRole}
                onRefresh={refreshWorkshopTimeline}
              />
            )}

            {timelineEligible && !phasesLoading && productionPhases.length === 0 && (
              <div className="rounded-xl border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                Aucune phase production (équipe production) trouvée pour le groupe de ce lot. Utilisez le flux par
                étapes du lot ci-dessous ou vérifiez la configuration des phases sur le cas.
              </div>
            )}

            {showLegacyWorkshopFlow && (
              <>
                <div className="rounded-xl border border-border bg-card p-3 space-y-3">
                  <WorkshopStepper status={batch.status} />
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-500 ease-out rounded-full"
                      style={{ width: `${Math.min(100, progressPct)}%` }}
                    />
                  </div>
                </div>

                <section className="rounded-xl border border-border bg-card p-4 space-y-3">
                  <h2 className="text-base font-semibold text-foreground">À faire maintenant</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {workshopStepHintForStatus(batch.status)}
                  </p>
                  {isWorkshopTerminal(batch.status) ? (
                    <div className="rounded-lg bg-success/15 border border-success/25 px-4 py-3 text-success font-medium text-center">
                      Ce lot est terminé — affichage en lecture seule.
                    </div>
                  ) : (
                    <Button
                      type="button"
                      className="w-full min-h-[52px] text-base font-semibold"
                      iconName="CheckCircle2"
                      iconPosition="right"
                      onClick={handleAdvance}
                      disabled={advancing || !getNextWorkshopStatus(batch.status)}
                    >
                      {advancing ? 'Enregistrement…' : 'Étape suivante ✓'}
                    </Button>
                  )}
                </section>
              </>
            )}

            <Button type="button" variant="outline" className="min-h-[48px] w-full" onClick={() => setSearchParams({})}>
              Scanner un autre lot
            </Button>
          </>
        )}
      </main>

      {showDocumentBar ? (
        <WorkshopDocumentBar
          batch={batch}
          caseId={batch?.case_id}
          iprData={iprData}
          iprLoading={iprLoading}
        />
      ) : null}
    </div>
  );
}

export default WorkshopLotPage;
